/**
 * Side Menu Module
 * @description 사이드 메뉴 초기화 - 헤더 타입에 따라 적절한 렌더러 적용
 */

import { smoothScroll, updateScrollArrows, updateScrollPadding } from '../../../core';
import { getSideMenuElements, getSideMenuType, type SideMenuType, setupArrowNavigation } from './base';
import { loadCategories } from './category-loader';
import { drawerRenderer, dropdownRenderer, megaMenuRenderer, type SideMenuRenderer } from './renderers';

export * from './types';

/**
 * 메뉴 타입에 따른 렌더러 반환
 */
const getRenderer = (type: SideMenuType): SideMenuRenderer | null => {
  switch (type) {
    case 'dropdown':
      return dropdownRenderer;
    case 'mega-menu':
      return megaMenuRenderer;
    case 'drawer':
      return drawerRenderer;
    default:
      return null;
  }
};

/**
 * 사이드 메뉴 초기화
 * 헤더의 data-upper-menu-type 및 data-upper-menu-category-type에 따라
 * 적절한 렌더러를 적용합니다.
 */
export const initSideMenu = async (): Promise<void> => {
  const menuType = getSideMenuType();
  if (!menuType) {
    return;
  }

  const elements = getSideMenuElements();
  if (!elements) {
    return;
  }

  const renderer = getRenderer(menuType);
  if (!renderer) {
    return;
  }

  const { list, leftArrow, rightArrow } = elements;

  // 카테고리 로드 (static: 전역변수, godo: API)
  try {
    const categories = await loadCategories();

    // 카테고리 렌더링
    if (categories.length > 0) {
      renderer.render(list, categories);
    }

    // 인터랙션은 항상 설정 (햄버거 버튼 이벤트 등)
    renderer.setupInteractions({ elements, categories });
  } catch (err) {
    console.error('Failed to load categories:', err);
    return;
  }

  // 화살표 네비게이션은 항상 설정
  const arrowElements = { list, leftArrow, rightArrow };
  const updateArrows = () => updateScrollArrows(arrowElements);
  const updatePadding = () => updateScrollPadding(arrowElements);
  const scroll = (direction: 'left' | 'right') => smoothScroll(list, direction);

  setupArrowNavigation(elements, updateArrows, updatePadding, scroll);
};
